/**
 * webscoket 组件封装
 */
import request from '@/utils/request';
import { Local } from '@/utils/storage';
import { PriceConfigResDto } from '@/views/UserCenter/pages/Wallet/type';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { WebSocketLike } from 'react-use-websocket/dist/lib/types';
import { logout, useAuth } from './AuthContext';

/**
 * WebSocket上下文类型定义，用于管理WebSocket连接状态和相关数据
 */
export interface WebSocketContextType {
  // 发送原始消息的方法
  sendMessage: (message: any) => void;
  // 最后接收到的原始消息
  lastMessage: MessageEvent<any> | null;
  // WebSocket连接的当前状态
  readyState: ReadyState;
  // 连接状态的文本描述
  connectionStatus: string;
  // 是否已连接的布尔值
  isConnected: boolean;
  // 发送JSON格式消息的方法
  sendJsonMessage: (message: any) => void;
  // 最后接收到的JSON格式消息
  lastJsonMessage: unknown;
  // 未读消息点的状态
  unReadPoint: boolean;
  // 更新未读消息点状态的方法
  updateUnReadPoint: (status: boolean) => void;
  // 消息队列列表的引用
  queueList: React.RefObject<string[]>;
  // 活动窗口的可见性状态
  activeVisiable: boolean;
  // 设置活动窗口可见性的方法
  setActiveVisiable: (status: boolean) => void;
  // 认证变更窗口的可见性状态
  authChangeVisiable: boolean;
  // 登出对象（可选）
  logoutObj?: Partial<WsMessage> | null;
  // 价格配置列表
  priceList: PriceConfigResDto[];
  // 用户详情信息
  userDetail: UserDetailResDto | null;
  // 设置用户详情信息的方法
  setUserDetail: (userDetail: UserDetailResDto) => void;
  // 查询用户详情信息的方法
  queryUserDetail: () => Promise<void>;
  // 未读消息点消息类型列表
  unReadPointMessageTypeList: MessageType[];
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

interface WebSocketProviderProps {
  children: React.ReactNode;
  options?: {
    // 是否重连
    shouldReconnect?: (closeEvent: CloseEvent) => boolean;
    // 重连次数
    reconnectAttempts?: number;
    // 间隔重连时间
    reconnectInterval?: number;
  };
}

export enum MessageType {
  /**
   * 查询消息
   */
  SearchNews = 1,
  /**
   * 账号消息
   */
  AccountNews = 0,
  /**
   * 授权消息
   */
  AuthNews = 2,
  /**
   * 贷后监控消息
   */
  PostLoanNews = 3,
  /**
   * 权限变更
   * @description 个人中心-角色管理中，更改了角色的权限，需要通知该角色下的所有用户，
   * 前端执行踢下线等操作，踢下线后需要重新登录。
   */
  Offline = 401,
  /**
   * 总后台价格状态变更
   */
  PriceStatus = 410,
}

export interface ActivityMsg {
  title: string;
  content: string;
}

/**
 * WebSocket 消息数据
 * @description 定义了WebSocket消息的基本结构，包括类型和内容。
 */
export interface WsMessage {
  /**
   * 消息内容，根据消息类型不同而内容也不同。
   */
  content?: string;
  /**
   * 消息类型
   */
  groupType?: MessageType;
  /**
   * 标题
   */
  title?: string;
  /**
   * 跳转地址
   */
  url?: string;
  userId?: string;
  userName?: string;
  timestamp?: number;
  /**
   * 支付状态 0 失败 1成功
   */
  status?: 0 | 1;
}

/**
 * 用户详情信息
 */
export interface UserDetailResDto {
  /**
   * 可用金币
   */
  balanceOfAccount?: number | string;
  /**
   * 客服的二维码
   */
  customerServiceUrl?: string;
  /**
   * 消息数量
   */
  notificationCount?: number;
  /**
   * 手机号
   */
  phone?: string;
  /**
   * 今日剩余查询次数
   */
  queryCount?: number;
  /**
   * 用户
   */
  realName?: string;
  /**
   * 角色
   */
  roleName?: string;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children, options = {} }) => {
  const { getScoketUrl, isAuthenticated } = useAuth();
  const [unReadPoint, setUnReadPoint] = useState<boolean>(false);
  // 活动消息队列
  const queueList = useRef<string[]>([]);
  // 判断活动弹窗显示
  const [activeVisiable, setActiveVisiable] = useState<boolean>(false);
  // 权限改变登出弹窗显示
  const [authChangeVisiable, setAuthChangeVisiable] = useState<boolean>(false);
  const webSocketRef = useRef<WebSocketLike>(null);
  const [shouldConnect, setShouldConnect] = useState(false);
  const [logoutObj, setLogoutObj] = useState<Partial<WsMessage> | null>(null);
  // 查询价格列表
  const [priceList, setPriceList] = useState<PriceConfigResDto[]>(Local.get('price_list') || []);
  // 用户详情信息
  const [userDetail, setUserDetail] = useState<UserDetailResDto | null>(null);
  // 用户详情loading
  const [userDetailLoading, setUserDetailLoading] = useState<boolean>(false);
  /**
   * 未读消息类型列表，用于判断是否显示未读点。
   */
  const unReadPointMessageTypeList = useMemo(
    () => [
      MessageType.AccountNews,
      MessageType.SearchNews,
      MessageType.AuthNews,
      MessageType.PostLoanNews,
    ],
    [],
  ); // 空依赖数组，因为 MessageType 是常量枚举

  /**
   * 获取未读消息数
   */
  const getUnReadCount = async () => {
    try {
      const data = await request.get<number>('UserCenter.notifyUnreadCount', {
        params: {},
        silent: true,
      });
      if (data > 0) {
        setUnReadPoint(true);
      }
    } catch (error) {
      console.error('获取未读消息数失败:', error);
    }
  };
  const {
    sendMessage: sendWebSocketMessage,
    lastMessage,
    readyState,
    sendJsonMessage,
    lastJsonMessage,
    getWebSocket,
  } = useWebSocket(
    getScoketUrl(),
    {
      shouldReconnect: options.shouldReconnect || (() => true),
      reconnectAttempts: options.reconnectAttempts || 5,
      reconnectInterval: options.reconnectInterval || 30000,
      share: true, // 共享连接
      onMessage: async (event) => {
        if (event.data) {
          const data: WsMessage = JSON.parse(event.data);
          // 设置未读红点消息
          if (unReadPointMessageTypeList.includes(data.groupType!)) {
            setUnReadPoint(true);
            // getUnReadCount();
            // queryUserDetail();
          }
          // 强制登出逻辑，踢下线后需要重新登录。
          if (data.groupType === MessageType.Offline) {
            setLogoutObj(data);
            setAuthChangeVisiable(true);
            logout();
            handleDisconnect();
            return;
          }
          if (data.groupType === MessageType.PriceStatus) {
            await getPriceList();
          }
        }
      },
      onOpen: () => {
        console.log('WebSocket 连接已建立');
        setLogoutObj(null);
        // showSuccess("连接成功")
      },
      onClose: () => console.log('WebSocket 连接已关闭'),
      onError: (event) => console.error('WebSocket 错误:', event),
      onReconnectStop: (numAttempts) => console.log(`重连停止，已尝试 ${numAttempts} 次`),
    },
    shouldConnect,
  );
  // 获取 WebSocket 实例
  useEffect(() => {
    const ws = getWebSocket();
    if (ws) {
      webSocketRef.current = ws;
    }
  }, [getWebSocket, readyState]);
  useEffect(() => {
    if (isAuthenticated) {
      setShouldConnect(true);
    } else {
      setShouldConnect(false);
    }
  }, [isAuthenticated]);
  const connectionStatus = {
    [ReadyState.CONNECTING]: '连接中',
    [ReadyState.OPEN]: '已连接',
    [ReadyState.CLOSING]: '关闭中',
    [ReadyState.CLOSED]: '已关闭',
    [ReadyState.UNINSTANTIATED]: '未实例化',
  }[readyState];

  const sendMessage = useCallback(
    (data: any) => {
      if (readyState === ReadyState.OPEN) {
        const message = typeof data === 'string' ? data : JSON.stringify(data);
        sendWebSocketMessage(message);
      } else {
        console.warn('WebSocket 未连接，无法发送消息');
      }
    },
    [readyState, sendWebSocketMessage],
  );

  const isConnected = readyState === ReadyState.OPEN;

  const updateUnReadPoint = useCallback((status: boolean) => {
    setUnReadPoint(status);
  }, []);

  // 手动关闭连接
  const handleDisconnect = useCallback(() => {
    const ws = webSocketRef.current || getWebSocket();
    if (ws?.readyState === ReadyState.OPEN) {
      ws.close(); // ✅ 直接调用 close 方法
    }
    setShouldConnect(false); // 阻止自动重连
    setLogoutObj(null);
  }, [getWebSocket]);
  /**
   * 获取用户详情信息
   */
  const queryUserDetail = useCallback(async () => {
    if (userDetailLoading) return;
    setUserDetailLoading(true);
    try {
      const data = await request.get<UserDetailResDto | null>('Layout.queryUserDetail', {
        silent: true,
      });
      if (!data) {
        // 没数据
        return;
      }
      data.balanceOfAccount = data.balanceOfAccount
        ? Number.parseFloat(data.balanceOfAccount!.toString())
            .toFixed(2)
            .replaceAll(/\B(?=(\d{3})+(?!\d))/g, ',')
        : '0.00';
      setUserDetail(data);
    } catch (error) {
      console.error('获取用户详情信息错误', error);
    }
    setUserDetailLoading(false);
  }, [userDetailLoading]);
  const contextValue = useMemo(
    () => ({
      sendMessage,
      lastMessage,
      readyState,
      connectionStatus,
      isConnected,
      sendJsonMessage,
      lastJsonMessage,
      unReadPoint,
      updateUnReadPoint,
      activeVisiable,
      setActiveVisiable,
      queueList,
      authChangeVisiable,
      logoutObj,
      priceList,
      userDetail,
      setUserDetail,
      queryUserDetail,
      unReadPointMessageTypeList,
    }),
    [
      sendMessage,
      lastMessage,
      readyState,
      connectionStatus,
      isConnected,
      sendJsonMessage,
      lastJsonMessage,
      unReadPoint,
      updateUnReadPoint,
      activeVisiable,
      setActiveVisiable,
      queueList,
      authChangeVisiable,
      logoutObj,
      priceList,
      userDetail,
      setUserDetail,
      queryUserDetail,
      unReadPointMessageTypeList,
    ],
  );

  /**
   * 获取价格配置列表
   */
  const getPriceList = async () => {
    const data = await request.get<PriceConfigResDto[]>('UserCenter.walletQueryPriceConfigPage', {
      silent: true,
    });
    Local.set('price_list', data);
    setPriceList(data);
  };

  useEffect(() => {
    if (isAuthenticated) {
      // getUnReadCount();
      // queryUserDetail();
    }
  }, [isAuthenticated]);
  return <WebSocketContext.Provider value={contextValue}>{children}</WebSocketContext.Provider>;
};

export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext 必须在 WebSocketProvider 内部使用');
  }
  return context;
};
